import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type {
  AdminListItem,
  PaginatedAuditLog,
  SettingsOverview,
} from '../services/types';
import { formatDate } from '../utils/format';
import { notifyApiResult } from '../utils/notify';
import { useAuth } from '../hooks/useAuth';

type Tab = 'overview' | 'profile' | 'admins' | 'audit';

const ROLES = ['admin', 'finance', 'support', 'readonly', 'superadmin'] as const;

const ROLE_HINTS: Record<string, string> = {
  readonly: 'Только просмотр',
  support: 'Тикеты + пользователи (без VPN/платежей/промо/тарифов)',
  finance: 'Платежи и выдача подписки (тарифы — только просмотр)',
  admin: 'Операционка: VPN, серверы, тикеты (без промо/тарифов и админов)',
  superadmin: 'Полный доступ, включая админов, промо и тарифы',
};

export function SettingsPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'overview';

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    if (t !== 'audit') {
      next.delete('page');
      next.delete('q');
      next.delete('action');
      next.delete('resource_type');
      next.delete('actor');
    }
    setSearchParams(next);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Настройки</h1>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${tab === 'overview' ? 'active' : ''}`}
          onClick={() => setTab('overview')}
        >
          Обзор
        </button>
        <button
          type="button"
          className={`tab ${tab === 'profile' ? 'active' : ''}`}
          onClick={() => setTab('profile')}
        >
          Профиль
        </button>
        {isSuperadmin && (
          <button
            type="button"
            className={`tab ${tab === 'admins' ? 'active' : ''}`}
            onClick={() => setTab('admins')}
          >
            Админы
          </button>
        )}
        <button
          type="button"
          className={`tab ${tab === 'audit' ? 'active' : ''}`}
          onClick={() => setTab('audit')}
        >
          Audit log
        </button>
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'profile' && <ProfileTab />}
      {tab === 'admins' && isSuperadmin && <AdminsTab />}
      {tab === 'audit' && <AuditTab searchParams={searchParams} setSearchParams={setSearchParams} />}
    </div>
  );
}

function ProfileTab() {
  const { user } = useAuth();
  const toast = useToast();
  const [displayName, setDisplayName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [quickReplies, setQuickReplies] = useState<
    Array<{ id: number; title: string; body: string; owner_telegram_id: number }>
  >([]);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');

  const reload = useCallback(async () => {
    const [profile, replies] = await Promise.all([
      api.getMyAdminProfile(),
      api.listSupportQuickReplies(),
    ]);
    if (profile.data) {
      const name = profile.data.display_name || '';
      setDisplayName(name);
      setSavedName(name);
    }
    if (replies.data) setQuickReplies(replies.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dirty = displayName.trim() !== savedName.trim();
  const previewName = displayName.trim() || null;

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const res = await api.updateMyAdminProfile({ display_name: displayName.trim() || null });
    setBusy(false);
    if (notifyApiResult(toast, res, 'Псевдоним сохранён') && res.data) {
      const name = res.data.display_name || '';
      setDisplayName(name);
      setSavedName(name);
    }
  };

  const addQuick = async (e: FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newBody.trim()) return;
    setBusy(true);
    const res = await api.createSupportQuickReply({
      title: newTitle.trim(),
      body: newBody.trim(),
    });
    setBusy(false);
    if (notifyApiResult(toast, res, 'Шаблон добавлен')) {
      setNewTitle('');
      setNewBody('');
      setAdding(false);
      void reload();
    }
  };

  const removeQuick = async (id: number) => {
    setBusy(true);
    const res = await api.deleteSupportQuickReply(id);
    setBusy(false);
    if (notifyApiResult(toast, res, 'Шаблон удалён')) void reload();
  };

  if (loading) return <p className="muted">Загрузка…</p>;

  const ownReplies = quickReplies.filter((q) => q.owner_telegram_id !== 0);
  const sharedReplies = quickReplies.filter((q) => q.owner_telegram_id === 0);

  return (
    <div className="settings-profile">
      <section className="card settings-profile-card">
        <div className="settings-profile-head">
          <div>
            <h3 className="settings-profile-title">Псевдоним в чате</h3>
            <p className="muted settings-profile-hint">
              Клиент видит его над вашими сообщениями в поддержке.
            </p>
          </div>
          <div className="settings-profile-meta muted">
            <span>{user?.role ?? '—'}</span>
            <span>·</span>
            <span>id {user?.telegram_user_id ?? '—'}</span>
          </div>
        </div>

        <form onSubmit={saveProfile} className="settings-profile-alias">
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={64}
            placeholder="Например: Анна"
            aria-label="Псевдоним"
          />
          <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>
            Сохранить
          </button>
        </form>

        <div className="settings-profile-preview" aria-label="Превью в чате">
          <div className="settings-profile-preview-label muted">Как увидит клиент</div>
          <div className="settings-profile-bubble">
            {previewName ? <div className="settings-profile-bubble-author">{previewName}</div> : null}
            <div className="settings-profile-bubble-text">Здравствуйте! Уже разбираюсь с вашим вопросом.</div>
            <div className="settings-profile-bubble-time">12:40</div>
          </div>
          {!previewName ? (
            <p className="muted settings-profile-preview-empty">Без псевдонима имя над сообщением не показывается.</p>
          ) : null}
        </div>
      </section>

      <section className="card settings-profile-card">
        <div className="settings-profile-head">
          <div>
            <h3 className="settings-profile-title">Быстрые ответы</h3>
            <p className="muted settings-profile-hint">
              Чипы над полем ввода в тикете — в один клик подставляют текст.
            </p>
          </div>
          {!adding ? (
            <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
              + Шаблон
            </button>
          ) : null}
        </div>

        {adding ? (
          <form onSubmit={addQuick} className="settings-profile-add">
            <input
              className="input"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={80}
              placeholder="Короткое название"
              required
              autoFocus
            />
            <textarea
              className="input settings-profile-add-body"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Текст ответа…"
              required
            />
            <div className="settings-profile-add-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Добавить
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => {
                  setAdding(false);
                  setNewTitle('');
                  setNewBody('');
                }}
              >
                Отмена
              </button>
            </div>
          </form>
        ) : null}

        {ownReplies.length === 0 && sharedReplies.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Шаблонов пока нет. Добавьте свой или используйте общие после деплоя миграции.
          </p>
        ) : (
          <ul className="settings-profile-replies">
            {ownReplies.map((qr) => (
              <li key={qr.id} className="settings-profile-reply">
                <div className="settings-profile-reply-main">
                  <div className="settings-profile-reply-title">{qr.title}</div>
                  <div className="settings-profile-reply-body muted">{qr.body}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => void removeQuick(qr.id)}
                >
                  Удалить
                </button>
              </li>
            ))}
            {sharedReplies.map((qr) => (
              <li key={qr.id} className="settings-profile-reply is-shared">
                <div className="settings-profile-reply-main">
                  <div className="settings-profile-reply-title">
                    {qr.title}
                    <span className="settings-profile-badge">общий</span>
                  </div>
                  <div className="settings-profile-reply-body muted">{qr.body}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState<SettingsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.getSettingsOverview();
      if (res.data) setData(res.data);
      setLoading(false);
    })();
  }, []);

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;
  if (!data) return <p style={{ color: 'var(--danger)' }}>Не удалось загрузить настройки</p>;

  return (
    <div className="grid-stats">
      <InfoCard label="API version">{data.api_version}</InfoCard>
      <InfoCard label="Environment">{data.environment}</InfoCard>
      <InfoCard label="Database">
        <span className={data.database === 'ok' ? 'status-ok' : 'status-error'}>{data.database}</span>
      </InfoCard>
      <InfoCard label="Redis">
        <span className={data.redis === 'ok' ? 'status-ok' : 'status-error'}>{data.redis}</span>
      </InfoCard>
      <InfoCard label="Bot username">{data.bot_username_configured ? 'настроен' : 'не задан'}</InfoCard>
      <InfoCard label="WG nodes">{data.wg_nodes_configured}</InfoCard>
      <InfoCard label="Servers cache">{data.runtime_servers_cached}</InfoCard>
      <InfoCard label="Session TTL">{data.session_ttl_seconds}s</InfoCard>
      <InfoCard label="Rate limit">
        {data.rate_limit_requests} / {data.rate_limit_window_seconds}s
      </InfoCard>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">URLs</div>
        <div style={{ fontSize: '0.875rem' }}>
          Panel: {data.admin_panel_url}
          <br />
          Portal: {data.user_portal_url}
          <br />
          Base path: {data.app_base_path}
        </div>
      </div>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">CORS origins</div>
        <div style={{ fontSize: '0.875rem' }}>{data.cors_origins.join(', ') || '—'}</div>
      </div>
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="stat-label">Panel migrations</div>
        <div style={{ fontSize: '0.875rem' }}>{data.panel_migrations_applied.join(', ') || '—'}</div>
      </div>
    </div>
  );
}

function AdminsTab() {
  const { user } = useAuth();
  const toast = useToast();
  const [admins, setAdmins] = useState<AdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [newId, setNewId] = useState('');
  const [newRole, setNewRole] = useState('support');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listAdmins();
    if (res.data) setAdmins(res.data);
    else if (res.error) setError(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const id = Number(String(newId).trim());
    if (!Number.isFinite(id) || id <= 0) {
      setError('Укажите корректный Telegram user ID');
      return;
    }
    const res = await api.upsertAdmin({
      telegram_user_id: id,
      role: newRole,
    });
    if (notifyApiResult(toast, res, (data) => `Админ ${data.telegram_user_id} добавлен`)) {
      setMessage(`Админ ${res.data!.telegram_user_id} добавлен`);
      setNewId('');
      load();
    } else if (res.error) setError(res.error.message);
  };

  const changeRole = async (admin: AdminListItem, role: string) => {
    if (admin.telegram_user_id === user?.telegram_user_id) return;
    const res = await api.updateAdmin(admin.telegram_user_id, { role });
    if (notifyApiResult(toast, res, () => `Роль обновлена: ${admin.telegram_user_id} → ${role}`)) {
      load();
    } else if (res.error) setError(res.error.message);
  };

  const toggleActive = async (admin: AdminListItem) => {
    if (admin.telegram_user_id === user?.telegram_user_id) return;
    const res = await api.updateAdmin(admin.telegram_user_id, { is_active: !admin.is_active });
    if (
      notifyApiResult(
        toast,
        res,
        () => `Админ ${admin.telegram_user_id} ${admin.is_active ? 'деактивирован' : 'активирован'}`,
      )
    ) {
      load();
    } else if (res.error) setError(res.error.message);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Добавить админа</h3>
        <p className="muted" style={{ fontSize: '0.85rem', marginTop: 0 }}>
          Для друга обычно хватает роли <code>support</code> (тикеты). Роль <code>admin</code> шире:
          VPN и серверы, но без промокодов, платежей и управления админами.
        </p>
        <form onSubmit={addAdmin} className="filters-row">
          <input
            className="input"
            placeholder="Telegram user ID"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            required
            style={{ width: 160 }}
          />
          <select className="select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary">
            Добавить
          </button>
        </form>
        <p className="muted" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
          {ROLE_HINTS[newRole] || ''}
        </p>
      </div>

      {message && <p style={{ color: 'var(--success)' }}>{message}</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Telegram ID</th>
                <th>Role</th>
                <th>Active</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const isSelf = a.telegram_user_id === user?.telegram_user_id;
                return (
                  <tr key={a.id}>
                    <td>{a.telegram_user_id}</td>
                    <td>
                      <select
                        className="select"
                        value={a.role}
                        disabled={isSelf}
                        onChange={(e) => changeRole(a, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={a.is_active ? 'badge badge-success' : 'badge badge-muted'}>
                        {a.is_active ? 'да' : 'нет'}
                      </span>
                    </td>
                    <td>{formatDate(a.last_login_at)}</td>
                    <td>
                      {!isSelf && (
                        <button type="button" className="btn btn-ghost" onClick={() => toggleActive(a)}>
                          {a.is_active ? 'Деактивировать' : 'Активировать'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditTab({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (p: URLSearchParams) => void;
}) {
  const [data, setData] = useState<PaginatedAuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const action = searchParams.get('action') || '';
  const resourceType = searchParams.get('resource_type') || '';
  const actorId = searchParams.get('actor') || '';
  const [qInput, setQInput] = useState(q);
  const [actionInput, setActionInput] = useState(action);
  const [resourceInput, setResourceInput] = useState(resourceType);
  const [actorInput, setActorInput] = useState(actorId);

  const ACTION_PRESETS = [
    '',
    'auth.login',
    'auth.logout',
    'vpn_key.create',
    'vpn_key.delete',
    'ovpn_key.create',
    'ovpn_key.delete',
    'subscription.grant',
    'payment.status_update',
    'promo.upsert',
    'promo.set_active',
    'servers.reload_config',
    'servers.sync_replicas',
    'admin.upsert',
    'admin.update',
  ];

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listAuditLog({
      page,
      page_size: 30,
      q: q || undefined,
      action: action || undefined,
      resource_type: resourceType || undefined,
      actor_telegram_id: actorId ? Number(actorId) : undefined,
    });
    if (res.data) setData(res.data);
    setLoading(false);
  }, [page, q, action, resourceType, actorId]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'audit');
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    if (actionInput) next.set('action', actionInput);
    else next.delete('action');
    if (resourceInput) next.set('resource_type', resourceInput);
    else next.delete('resource_type');
    if (actorInput) next.set('actor', actorInput);
    else next.delete('actor');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'audit');
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div>
      <form onSubmit={applyFilters} className="filters-row">
        <input
          className="input"
          placeholder="Поиск: action, resource, actor…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <select
          className="select"
          value={actionInput}
          onChange={(e) => setActionInput(e.target.value)}
          aria-label="Action"
        >
          <option value="">Все actions</option>
          {ACTION_PRESETS.filter(Boolean).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="resource_type"
          value={resourceInput}
          onChange={(e) => setResourceInput(e.target.value)}
        />
        <input
          className="input"
          placeholder="Actor Telegram ID"
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
        />
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && data && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Время</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      style={{ cursor: row.before_json || row.after_json ? 'pointer' : undefined }}
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                    >
                      <td>{row.id}</td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        {row.actor_telegram_id}
                        <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({row.actor_role})</span>
                      </td>
                      <td>
                        <code style={{ fontSize: '0.75rem' }}>{row.action}</code>
                      </td>
                      <td>
                        {row.resource_type ?? '—'}
                        {row.resource_id ? ` / ${row.resource_id}` : ''}
                      </td>
                      <td>{row.ip ?? '—'}</td>
                    </tr>
                    {expandedId === row.id && (row.before_json || row.after_json) && (
                      <tr>
                        <td colSpan={6}>
                          <pre
                            style={{
                              margin: 0,
                              fontSize: '0.75rem',
                              overflow: 'auto',
                              maxHeight: 200,
                              background: 'var(--bg)',
                              padding: '0.5rem',
                              borderRadius: 'var(--radius)',
                            }}
                          >
                            {row.before_json && `before: ${JSON.stringify(row.before_json, null, 2)}\n`}
                            {row.after_json && `after: ${JSON.stringify(row.after_json, null, 2)}`}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="pagination">
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span style={{ color: 'var(--text-muted)' }}>
                {page} / {data.pages} ({data.total})
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}
